import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint, FaSearch } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import { clientActions } from '../../sagas/clientSlice';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import { transporteurActions } from '../../sagas/transporteurSlice';
import { companyActions } from '../../sagas/companySlice';
import { entrepotActions } from '../../sagas/entrepotSlice';

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const { companies } = useSelector(state => state.companies);
  const { entrepots } = useSelector(state => state.entrepots);
  const defaultCompanyId = company?.id || null;

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [filters, setFilters] = useState({
    companyId: null,
    clientId: null,
    transporteurId: null,
    entrepotId: null,
    chantierId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: ''
  });

  useEffect(() => {
    dispatch(saleActions.clearClientReport());
    generateClientReport()
  }, [filters.clientId, filters.companyId, filters.transporteurId, filters.entrepotId, filters.chantierId, filters.dateDebut, filters.dateFin]);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
      return parseISO(dateString);
    }

    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  const getPoutrelleNueLength = (designation) => {
    const match = String(designation || "")
      .trim()
      .match(/^poutrelle\s+nue\s+(\d+(?:[.,]\d+)?)\s*\/\s*\d+\b/i);

    return match ? Number(match[1].replace(",", ".")) : null;
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isInteger(number) ? number.toString() : number.toFixed(2);
  };

  // Client report state
  const { clients, loading } = useSelector(state => state.clients);
  const { clientReport, clientReportLoading, clientReportError } = useSelector(state => state.sales);
  const [selectedClient, setSelectedClient] = useState('');
  const clientReportRef = useRef(null);
  const { transporteurs } = useSelector(state => state.transporteurs);

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
    chantiers: client.chantiers || []
  })) ?? [];

  const clientOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...clientOptions
  ];

  const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
    value: chantier.id,
    label: `${chantier.code} - ${chantier.nom}`,
    ...chantier
  })) || [];

  const chantierOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...chantierOptions
  ];

  const companyOptions = companies?.map(item => ({
    value: item.id,
    label: item.name,
  })) ?? [];

  const companyOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...companyOptions
  ];

  const selectedCompanyInfo =
    companies?.find(item => Number(item.id) === Number(filters.companyId))
    || clientReport?.company
    || company;

  const resolveNoteCompanyName = (note) =>
    note?.companyName
    || note?.company?.name
    || companies?.find(item => Number(item.id) === Number(note?.companyId))?.name
    || selectedCompanyInfo?.name
    || '-';

  // Effect for fetching data
  useEffect(() => {
    if (clients.length === 0) {
      // Fetch clients for client report
      const fetchClients = async () => {
        dispatch(clientActions.search({
          query: ""
        }));
      };

      fetchClients();
    }
    dispatch(transporteurActions.list());
    dispatch(companyActions.getAll({}));
    dispatch(entrepotActions.list());

  }, []);

  useEffect(() => {
    if (defaultCompanyId && filters.companyId == null) {
      setFilters(prevFilters => ({
        ...prevFilters,
        companyId: defaultCompanyId
      }));
    }
  }, [defaultCompanyId]);

  const debouncedSearch = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  }


  // Generate client report
  const generateClientReport = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  };


  const exportClientReportToPDF = (withPrices = true) => {
    if (!clientReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 8;
    const footerHeight = 16;
    const tableWidth = pageWidth - margin * 2;

    // ---- THEME ----
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };
    const negativeRed = { r: 231, g: 76, b: 60 };

    // ---------------- HEADER ----------------
    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RAPPORT VENTE CLIENT", pageWidth / 2, 15, { align: "center" });

      const from = format(parseDate(clientReport.period.from), "dd/MM/yyyy", { locale: fr });
      const to = format(parseDate(clientReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Période : du ${from} au ${to}`, pageWidth / 2, 22, { align: "center" });
    };

    // ---------------- FOOTER ----------------
    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le : ${clientReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(selectedCompanyInfo?.name || company?.name || '', margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    // ---- FIRST PAGE ----
    addPageHeader();

    // ---------------- COMPANY INFO ----------------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(selectedCompanyInfo?.name || company?.name || '', margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse : ${selectedCompanyInfo?.address || '-'}`, margin, 47);
    doc.text(`Tél : ${company.phone1} | Email : ${company.email}`, margin, 52);
    doc.text(`RC : ${selectedCompanyInfo?.rc || '-'} | IF : ${selectedCompanyInfo?.identificationFiscale || '-'} | ICE : ${selectedCompanyInfo?.ice || '-'}`, margin, 57);

    // ---------------- CLIENT BOX ----------------
    if (clientReport.client) {
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("CLIENT", pageWidth - margin - 75, 42);
      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.text(`Nom : ${clientReport.client.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone : ${clientReport.client.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code : ${clientReport.client.code}`, pageWidth - margin - 75, 60);
    }

    // ---------------- TABLE ----------------
    const headers = withPrices
      ? ["N° BL", "Date", "Entrepôt", "Désignation", "Qté", "UM", "PU", "Total"]
      : ["N° BL", "Date", "Entrepôt", "Désignation", "Qté", "UM"];

    headers.splice(2, 0, "Chantier");

    const rows = clientReport.deliveryNotes.map((bl) =>
      withPrices
        ? [
          bl.blNumber,
          format(parseDate(bl.date), "dd/MM/yyyy", { locale: fr }),
          bl.chantier?.nom || "N/A",
          bl.entrepot || "N/A",
          bl.designation,
          bl.quantity.toString(),
          getPoutrelleNueLength(bl.designation) ? formatNumber(getPoutrelleNueLength(bl.designation) * Number(bl.quantity || 0)) : "",
          bl.unit,
          formatPrice(bl.unitPrice, false),
          formatPrice(bl.lineTotal, false),
        ]
        : [
          bl.blNumber,
          format(parseDate(bl.date), "dd/MM/yyyy", { locale: fr }),
          bl.chantier?.nom || "N/A",
          bl.entrepot || "N/A",
          bl.designation,
          bl.quantity.toString(),
          getPoutrelleNueLength(bl.designation) ? formatNumber(getPoutrelleNueLength(bl.designation) * Number(bl.quantity || 0)) : "",
          bl.unit,
        ]
    );

    headers.splice(6, 0, "ML");

	    const columnStyles = withPrices
	      ? {
	        0: { cellWidth: 20 },
        1: { cellWidth: 20 },
        2: { cellWidth: 32 },
        3: { cellWidth: 28 },
        4: { cellWidth: 92 },
        5: { cellWidth: 16, halign: "center" },
        6: { cellWidth: 16, halign: "center" },
        7: { cellWidth: 17, halign: "center" },
        8: { cellWidth: 20, halign: "right" },
        9: { cellWidth: 20, halign: "right" },
      }
      : {
        0: { cellWidth: 22 },
        1: { cellWidth: 22 },
        2: { cellWidth: 36 },
        3: { cellWidth: 30 },
        4: { cellWidth: 121 },
        5: { cellWidth: 18, halign: "center" },
        6: { cellWidth: 16, halign: "center" },
	        7: { cellWidth: 16, halign: "center" },
	      };

	    autoTable(doc, {
	      startY: 70,
	      tableWidth,
	      head: [headers],
	      body: rows,
      theme: "grid",
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b],
      },
      styles: {
	        cellPadding: withPrices ? 1.5 : 1.9,
	        fontSize: withPrices ? 8 : 8.3,
        textColor: [0, 0, 0],
        lineColor: [210, 214, 220],
        lineWidth: 0.15,
        overflow: "linebreak",
        valign: "middle",
      },
	      columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 0) {
          data.cell.styles.fillColor = [22, 101, 52];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      },
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
	      },
	    });

    const detailItems = clientReport.deliveryNotes || [];
    const detailTotalQuantity = detailItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const detailTotalMl = detailItems.reduce((sum, item) => {
      const length = getPoutrelleNueLength(item.designation);
      return sum + (length ? length * Number(item.quantity || 0) : 0);
    }, 0);
    const detailTotalAmount = detailItems.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const detailSummaryRows = [[
      detailItems.length.toString(),
      formatNumber(detailTotalQuantity),
      formatNumber(detailTotalMl),
      withPrices ? formatPrice(detailTotalAmount, false) : "-",
    ]];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Lignes", "Quantite totale", "Total ML", "Montant total"]],
      body: detailSummaryRows,
      theme: "grid",
      styles: { cellPadding: 2.2, fontSize: 9, textColor: [0, 0, 0], halign: "center" },
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 75 },
        2: { cellWidth: 70 },
        3: { cellWidth: 76 },
      },
      tableWidth,
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      },
    });

	    // ---------------- SUMMARY (ONLY IF WITH PRICES) ----------------
	    if (withPrices && clientReport.summary) {
      const s = clientReport.summary;
      let y = doc.lastAutoTable.finalY + 12;

      if (y + 58 > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        addPageFooter();
        y = 40;
      }

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, 90, 40, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÉSUMÉ", margin + 5, y + 7);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(black.r, black.g, black.b);

      doc.text("Montant Total Consommé :", margin + 5, y + 15);
      doc.text(formatPrice(s.totalConsome, false), margin + 85, y + 15, { align: "right" });

      doc.text("Solde Avant :", margin + 5, y + 22);
      doc.text(formatPrice(s.soldeAvant, false), margin + 85, y + 22, { align: "right" });

      doc.text("Avance :", margin + 5, y + 29);
      doc.text(formatPrice(s.avance, false), margin + 85, y + 29, { align: "right" });

      doc.text("Avoir :", margin + 5, y + 36);
      doc.text(formatPrice(s.avoir, false), margin + 85, y + 36, { align: "right" });

      const net = parseFloat(s.netApaye);
      const netY = y + 55;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(net < 0 ? negativeRed.r : primaryGreen.r, net < 0 ? negativeRed.g : primaryGreen.g, net < 0 ? negativeRed.b : primaryGreen.b);
      doc.text("Net à payer :", margin, netY);
      doc.text(formatPrice(net, false), margin + 60, netY);
    }

    // ---------------- OPEN IN NEW TAB ----------------
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const exportClientRecapToPDF = (withPrices = true) => {
    if (!clientReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 8;
    const footerHeight = 16;
    const tableWidth = pageWidth - margin * 2;

    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("RECAP VENTE CLIENT", pageWidth / 2, 15, { align: "center" });

      const from = format(parseDate(clientReport.period.from), "dd/MM/yyyy", { locale: fr });
      const to = format(parseDate(clientReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Période : du ${from} au ${to}`, pageWidth / 2, 22, { align: "center" });
    };

    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le : ${clientReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(selectedCompanyInfo?.name || company?.name || '', margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    addPageHeader();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(selectedCompanyInfo?.name || company?.name || '', margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse : ${selectedCompanyInfo?.address || '-'}`, margin, 47);
    doc.text(`Tél : ${company.phone1} | Email : ${company.email}`, margin, 52);
    doc.text(`RC : ${selectedCompanyInfo?.rc || '-'} | IF : ${selectedCompanyInfo?.identificationFiscale || '-'} | ICE : ${selectedCompanyInfo?.ice || '-'}`, margin, 57);

    if (clientReport.client) {
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("CLIENT", pageWidth - margin - 75, 42);
      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.text(`Nom : ${clientReport.client.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone : ${clientReport.client.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code : ${clientReport.client.code}`, pageWidth - margin - 75, 60);
    }

    const recapMap = new Map();
    (clientReport.deliveryNotes || []).forEach((bl) => {
      const clientName = bl.clientName || clientReport?.client?.name || "N/A";
      const chantierName = bl.chantier?.nom || "N/A";
      const entrepotName = bl.entrepot || "N/A";
      const designation = bl.designation || "-";
      const unit = bl.unit || "";
      const unitPrice = Number(bl.unitPrice || 0);
      const quantity = Number(bl.quantity || 0);
      const lineTotal = Number(bl.lineTotal || unitPrice * quantity || 0);
      const poutrelleLength = getPoutrelleNueLength(designation);
      const mlQuantity = poutrelleLength ? poutrelleLength * quantity : 0;
      const key = withPrices
        ? `${clientName}__${designation}__${unit}__${unitPrice}`
        : `${clientName}__${designation}__${unit}`;

      if (!recapMap.has(key)) {
        recapMap.set(key, {
          clientName,
          designation,
          unit,
          unitPrice,
          quantity: 0,
          lineTotal: 0,
          mlQuantity: 0,
          mlAmount: 0,
          chantiers: new Set(),
          entrepots: new Set(),
          blNumbers: new Set(),
        });
      }

      const current = recapMap.get(key);
      current.quantity += quantity;
      current.lineTotal += lineTotal;
      current.mlQuantity += mlQuantity;
      current.mlAmount += mlQuantity > 0 ? lineTotal : 0;
      current.chantiers.add(chantierName);
      current.entrepots.add(entrepotName);
      if (bl.blNumber) current.blNumbers.add(bl.blNumber);
    });

    const recapRows = Array.from(recapMap.values()).map((item) =>
      withPrices
        ? [
          item.clientName,
          Array.from(item.chantiers).join(", "),
          Array.from(item.entrepots).join(", "),
          Array.from(item.blNumbers).join(", "),
          item.designation,
          item.quantity.toString(),
          item.mlQuantity > 0 ? formatNumber(item.mlQuantity) : "",
          item.unit,
          formatPrice(item.unitPrice, false),
          formatPrice(item.lineTotal, false),
        ]
        : [
          item.clientName,
          Array.from(item.chantiers).join(", "),
          Array.from(item.entrepots).join(", "),
          Array.from(item.blNumbers).join(", "),
          item.designation,
          item.quantity.toString(),
          item.mlQuantity > 0 ? formatNumber(item.mlQuantity) : "",
          item.unit,
        ]
    );

    const headers = withPrices
      ? ["Entreprise", "Chantier(s)", "Entrepôt(s)", "BL(s)", "Désignation", "Qté", "UM", "PU", "Total"]
      : ["Entreprise", "Chantier(s)", "Entrepôt(s)", "BL(s)", "Désignation", "Qté", "UM"];
    headers.splice(6, 0, "ML");

	    const columnStyles = withPrices
	      ? {
	        0: { cellWidth: 32 },
        1: { cellWidth: 30 },
        2: { cellWidth: 26 },
        3: { cellWidth: 32 },
        4: { cellWidth: 80 },
        5: { cellWidth: 14, halign: "center" },
        6: { cellWidth: 14, halign: "center" },
        7: { cellWidth: 15, halign: "center" },
        8: { cellWidth: 19, halign: "right" },
        9: { cellWidth: 19, halign: "right" },
      }
      : {
        0: { cellWidth: 36 },
        1: { cellWidth: 36 },
        2: { cellWidth: 30 },
        3: { cellWidth: 38 },
        4: { cellWidth: 97 },
        5: { cellWidth: 16, halign: "center" },
        6: { cellWidth: 14, halign: "center" },
	        7: { cellWidth: 14, halign: "center" },
	      };

	    autoTable(doc, {
	      startY: 70,
	      tableWidth,
	      head: [headers],
	      body: recapRows,
      theme: "grid",
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b],
      },
      styles: {
	        cellPadding: withPrices ? 1.5 : 1.9,
	        fontSize: withPrices ? 8 : 8.3,
        textColor: [0, 0, 0],
        lineColor: [210, 214, 220],
        lineWidth: 0.15,
        overflow: "linebreak",
        valign: "middle",
      },
	      columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 0) {
          data.cell.styles.fillColor = [22, 101, 52];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.halign = "center";
        }
      },
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
	      },
	    });

    const recapItems = Array.from(recapMap.values());
    const totalQuantity = recapItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = recapItems.reduce((sum, item) => sum + item.lineTotal, 0);
    const totalMl = recapItems.reduce((sum, item) => sum + item.mlQuantity, 0);
    const totalMlAmount = recapItems.reduce((sum, item) => sum + item.mlAmount, 0);
    const recapSummaryRows = [[
      formatNumber(totalMl),
      withPrices ? formatPrice(totalMlAmount, false) : "-",
      formatNumber(totalQuantity),
      withPrices ? formatPrice(totalAmount, false) : "-",
    ]];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["ML total", "Montant ML", "Quantite totale", "Montant total"]],
      body: recapSummaryRows,
      theme: "grid",
      styles: { cellPadding: 2.2, fontSize: 9, textColor: [0, 0, 0], overflow: "linebreak", halign: "center" },
      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 75 },
        2: { cellWidth: 70 },
        3: { cellWidth: 76 },
      },
      tableWidth,
      margin: { left: margin, right: margin, top: 35, bottom: footerHeight },
      didDrawPage: () => {
        addPageHeader();
        addPageFooter();
      },
    });

	    if (false) {
	      const recapItems = Array.from(recapMap.values());
	      const totalMl = recapItems.reduce((sum, item) => sum + item.mlQuantity, 0);
	      const totalMlAmount = recapItems.reduce((sum, item) => sum + item.mlAmount, 0);
      const summaryHeight = withPrices ? 42 : 35;
      const summaryTopMargin = 15;
      const summaryBottomMargin = 10;
      let y = doc.lastAutoTable.finalY + summaryTopMargin;

      if (y + summaryHeight + summaryBottomMargin > pageHeight - footerHeight) {
        doc.addPage();
        addPageHeader();
        addPageFooter();
        y = 40;
      }

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, 90, summaryHeight, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÉSUMÉ", margin + 5, y + 7);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(black.r, black.g, black.b);
      doc.text("Quantité totale :", margin + 5, y + 16);
	      doc.setFillColor(255, 255, 255);
	      doc.rect(margin + 4, y + 12, 82, 6, "F");
	      doc.setTextColor(black.r, black.g, black.b);
	      doc.text("Uniquement articles Poutrelle nue x.xx / yy", margin + 5, y + 16);
	      doc.text("Total ML :", margin + 5, y + 25);
	      doc.text(formatNumber(totalMl), margin + 85, y + 25, { align: "right" });
	      if (withPrices) {
	        doc.text("Montant total :", margin + 5, y + 34);
	        doc.text(formatPrice(totalMlAmount, false), margin + 85, y + 34, { align: "right" });
	      }
	      if (totalMl === 0) {
	        doc.setFontSize(7);
	        doc.text("Aucune ligne ML detectee dans ce recap.", margin + 5, y + (withPrices ? 40 : 32));
	      }
    }

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };



  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];

  const entrepotOptions = entrepots?.map(entrepot => ({
    value: entrepot.id,
    label: `${entrepot.code} - ${entrepot.libelle}`,
  })) ?? [];


  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des ventes
            </h5>

            <div className="d-flex align-items-center gap-2">
              <div style={{ minWidth: '260px' }}>
                <Select
                  id="companyId"
                  options={companyOptionsWithAll}
                  value={companyOptionsWithAll.find(option => option.value === filters.companyId) || null}
                  onChange={(selectedOption) => setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }))}
                  placeholder={t('company.selectCompany', 'Sélectionner une entreprise')}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </div>
              <Button
                color="success"
                size="sm"
                onClick={generateClientReport}
                disabled={clientReportLoading}
              >
                {clientReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" />
                    Générer
                  </>
                )}
              </Button>

              {clientReport && (
                <UncontrolledDropdown>
                  <DropdownToggle color="secondary" size="sm" caret>
                    <FaDownload className="me-1" />
                    Export PDF
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => exportClientReportToPDF(true)}>
                      📄 Export AVEC prix
                    </DropdownItem>
                    <DropdownItem onClick={() => exportClientReportToPDF(false)}>
                      📄 Export SANS prix
                    </DropdownItem>
                    <DropdownItem divider />
                    <DropdownItem onClick={() => exportClientRecapToPDF(true)}>
                      📄 Récap AVEC prix
                    </DropdownItem>
                    <DropdownItem onClick={() => exportClientRecapToPDF(false)}>
                      📄 Récap SANS prix
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>

              )}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col lg={2} md={2} sm={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1">{t('reglement.search')}</Label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder={t('bonLivraison.searchPlaceholder')}
                    value={filters.query}
                    onChange={e => {
                      const value = e.target.value.toUpperCase();
                      setFilters(prevFilters => ({
                        ...prevFilters,
                        query: value
                      }));
                    }}
                    className="text-uppercase"
                    style={{ textTransform: 'uppercase' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        debouncedSearch(e.target.value.toUpperCase());
                      }
                    }}
                  />
                </div>
              </FormGroup>
            </Col>
            <Col md={2} className="d-none">
              <FormGroup>
                <Label htmlFor="companyId" className="form-label">
                  {t('company.company', 'Entreprise')}
                </Label>
                <Select
                  id="companyId"
                  options={companyOptionsWithAll}
                  value={companyOptionsWithAll.find(option => option.value === filters.companyId) || null}
                  onChange={(selectedOption) => setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }))}
                  placeholder={t('company.selectCompany', 'Sélectionner une entreprise')}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="clientSelect" className="form-label">
                  <FaUser className="me-1" /> Client
                </Label>
                <Select
                  id="clientId"
                  options={clientOptionsWithAll}
                  value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                    setSelectedClient(selectedOption);
                  }}
                  placeholder={t('bonLivraison.selectClient')}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup className="mb-0">
                <Label htmlFor="chantierId">
                  {t('chantier.title', 'Chantier')}:
                </Label>
                <Select
                  id="chantierId"
                  options={chantierOptionsWithAll}
                  value={chantierOptionsWithAll.find(option => option.value === filters?.chantierId) || null}
                  onChange={(selectedOption) => setFilters(f => ({ ...f, chantierId: selectedOption ? selectedOption.value : null }))}
                  placeholder={t('chantier.select', 'Sélectionner...')}
                  isSearchable
                  isClearable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col md="2">
              <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
              <Select
                id="transporteurId"
                options={transporteurOptions}
                value={transporteurOptions.find(option => option.value === filters.transporteurId)}
                onChange={(selectedOption) => setFilters(f => ({ ...f, transporteurId: selectedOption ? selectedOption.value : null }))}
                placeholder={t('bonLivraison.selectTransporteur')}
                isSearchable
                isClearable
              />

            </Col>
            <Col md="2">
              <Label htmlFor="entrepotId">{t('bonLivraison.entrepot', 'Entrepôt')}:</Label>
              <Select
                id="entrepotId"
                options={entrepotOptions}
                value={entrepotOptions.find(option => option.value === filters.entrepotId) || null}
                onChange={(selectedOption) => setFilters(f => ({ ...f, entrepotId: selectedOption ? selectedOption.value : null }))}
                placeholder={t('bonLivraison.selectEntrepot', 'Sélectionner...')}
                isSearchable
                isClearable
                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 })
                }}
              />

            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>

          </Row>

          {/* Client Report Content */}
          <div ref={clientReportRef}>
            {clientReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {clientReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {clientReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {clientReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">

                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BL</th>
                          <th className="text-center">N° BC</th>
	                          <th className="text-center">Date</th>
	                          <th className="text-center">Utilisateur</th>
	                          <th className="text-center">{t('company.company', 'Entreprise')}</th>
	                          <th className="text-center">Client</th>
                          <th className="text-center">Chantier</th>
                          <th className="text-center">Entrepôt</th>
                          <th className="text-center">Mode P.</th>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">Transporteur</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>

                        </tr>
                      </thead>
                      <tbody>
                        {clientReport.deliveryNotes.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.blNumber}</td>
                            <td className="text-center fw-semibold">{note.bcNumber}</td>
	                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
	                            <td className="text-center">{note.createdBy}</td>
	                            <td className="text-center">{resolveNoteCompanyName(note)}</td>
	                            <td className="text-center">{note.client ? `${note.client.name}(${note.client.code})` : "Passager"}</td>
                            <td className="text-center">{note.chantier ? note.chantier.nom : 'N/A'}</td>
                            <td className="text-center">{note.entrepot || 'N/A'}</td>
                            <td className="ps-2">{note.modeReglement}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{note.transporteur ? `${note.transporteur.libelle} (${note.transporteur.matricule})` : 'N/A'}</td>
                            <td className="text-center">{note.unit}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
	                          <td colSpan="10" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {clientReport.deliveryNotes.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(clientReport.deliveryNotes.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>


                {clientReport && clientReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(clientReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(clientReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(clientReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                clientReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          clientReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>}

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{clientReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {clientReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un client et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default VentesStatistiques;
